import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { requestNotificationPermission, onForegroundMessage, initializeMessaging } from '../lib/firebase';
import { registerPushToken, unregisterPushToken } from '../services/api';
import { toast } from '../hooks/use-toast';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [fcmToken, setFcmToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  const initMessaging = useCallback(() => {
    initializeMessaging();
  }, []);

  useEffect(() => {
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
      setSupported(false);
    }
  }, []);

  useEffect(() => {
    initMessaging();
  }, [initMessaging]);

  // Listen for foreground messages
  useEffect(() => {
    if (permission === 'granted' && currentUser) {
      onForegroundMessage((payload) => {
        
        const title = payload.notification?.title || payload.data?.title || 'New Notification';
        const body = payload.notification?.body || payload.data?.body || '';
        
        // Show toast notification
        toast({
          title: title,
          description: body,
          duration: 5000,
        });
        
        // Also show browser notification if page is not focused
        if (document.visibilityState !== 'visible' && typeof Notification !== 'undefined') {
          new Notification(title, {
            body: body,
            icon: '/assets/logo.png',
            tag: payload.data?.type || 'default'
          });
        }
      });
    }
  }, [permission, currentUser]);

  // Enable notifications
  const enableNotifications = useCallback(async () => {
    if (!supported) {
      toast({
        title: 'Not Supported',
        description: 'Push notifications are not supported in this browser',
        variant: 'destructive'
      });
      return false;
    }

    setLoading(true);
    try {
      // Timeout after 15s — getToken can hang on some Android devices
      const token = await Promise.race([
        requestNotificationPermission(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000))
      ]);
      
      if (token) {
        setFcmToken(token);
        setPermission('granted');
        
        // Register token with backend
        if (currentUser) {
          await registerPushToken(token);
        }
        
        toast({
          title: 'Notifications Enabled',
          description: 'You will now receive push notifications',
        });
        return true;
      } else {
        setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
          toast({
            title: 'Permission Denied',
            description: 'Please enable notifications in your browser settings',
            variant: 'destructive'
          });
        }
        return false;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message === 'timeout'
          ? 'Notification setup timed out. Try refreshing the page and enabling again.'
          : 'Failed to enable notifications. Check browser settings.',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentUser, supported]);

  // Disable notifications
  const disableNotifications = useCallback(async () => {
    try {
      if (fcmToken && currentUser) {
        await unregisterPushToken(fcmToken);
      }
      setFcmToken(null);
      toast({
        title: 'Notifications Disabled',
        description: 'You will no longer receive push notifications',
      });
      return true;
    } catch (error) {
      return false;
    }
  }, [fcmToken, currentUser]);

  // Auto-register token when user logs in
  useEffect(() => {
    if (currentUser && fcmToken) {
      registerPushToken(fcmToken).catch(() => {});
    }
  }, [currentUser, fcmToken]);

  // Permission already granted on this device → silently restore the FCM token on
  // every page load (no browser prompt) so isEnabled survives reloads/new sessions.
  const [restoring, setRestoring] = useState(() => typeof Notification !== 'undefined' && Notification.permission === 'granted');
  useEffect(() => {
    if (!currentUser || !supported || permission !== 'granted' || fcmToken) {
      if (permission !== 'granted' || fcmToken) setRestoring(false);
      return;
    }
    let cancelled = false;
    setRestoring(true);
    requestNotificationPermission()
      .then((token) => { if (!cancelled && token) setFcmToken(token); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRestoring(false); });
    return () => { cancelled = true; };
  }, [currentUser, supported, permission, fcmToken]);

  const value = {
    permission,
    fcmToken,
    loading,
    supported,
    restoring,
    isEnabled: permission === 'granted' && !!fcmToken,
    enableNotifications,
    disableNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
